import { BigNumber } from "@ethersproject/bignumber";
import { PollingInterval } from "uniswap/src/constants/misc";
import { Chain } from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import {
  GQL_MAINNET_CHAINS,
  GQL_TESTNET_CHAINS,
  UNIVERSE_CHAIN_INFO,
  getChainInfo
} from "uniswap/src/features/chains/chainInfo";
import {
  ALL_CHAIN_IDS,
  NetworkLayer,
  SUPPORTED_CHAIN_IDS,
  SUPPORTED_TESTNET_CHAIN_IDS,
  UniverseChainId
} from "uniswap/src/features/chains/types";
export function toSupportedChainId(chainId) {
  if (!chainId || !ALL_CHAIN_IDS.map((c) => c.toString()).includes(chainId.toString())) {
    return null;
  }
  return parseInt(chainId.toString(), 10);
}
export function chainSupportsGasEstimates(chainId) {
  return getChainInfo(chainId).supportsGasEstimates;
}
export function getChainLabel(chainId) {
  return getChainInfo(chainId).label;
}
export function isTestnetChain(chainId) {
  return Boolean(getChainInfo(chainId)?.testnet);
}
export function getChainIdByInfuraPrefix(prefix) {
  return Object.values(UNIVERSE_CHAIN_INFO).find((info) => info.infuraPrefix === prefix)?.id;
}
export function isBackendSupportedChainId(chainId) {
  const info = getChainInfo(chainId);
  return info.backendChain.backendSupported && !info.backendChain.isSecondaryChain;
}
export function isBackendSupportedChain(chain) {
  const chainId = fromGraphQLChain(chain);
  if (!chainId) {
    return false;
  }
  return chainId && isBackendSupportedChainId(chainId);
}
export function chainIdToHexadecimalString(chainId) {
  return BigNumber.from(chainId).toHexString();
}
export function hexadecimalStringToInt(hex) {
  return parseInt(hex, 16);
}
export function isL2ChainId(chainId) {
  return chainId !== void 0 && getChainInfo(chainId).networkLayer === NetworkLayer.L2;
}
export function isMainnetChainId(chainId) {
  return chainId === UniverseChainId.Mainnet || chainId === UniverseChainId.Sepolia;
}
export function toGraphQLChain(chainId) {
  return getChainInfo(chainId).backendChain.chain;
}
const GRAPHQL_CHAIN_TO_UNIVERSE = {
  [Chain.Ethereum]: UniverseChainId.Mainnet,
  [Chain.Arbitrum]: UniverseChainId.ArbitrumOne,
  [Chain.Avalanche]: UniverseChainId.Avalanche,
  [Chain.Base]: UniverseChainId.Base,
  [Chain.Bnb]: UniverseChainId.Bnb,
  [Chain.Blast]: UniverseChainId.Blast,
  [Chain.Celo]: UniverseChainId.Celo,
  [Chain.Hyper]: UniverseChainId.HyperMainnet,
  [Chain.MonadTestnet]: UniverseChainId.MonadTestnet,
  [Chain.Optimism]: UniverseChainId.Optimism,
  [Chain.Polygon]: UniverseChainId.Polygon,
  [Chain.EthereumSepolia]: UniverseChainId.Sepolia,
  [Chain.Unichain]: UniverseChainId.Unichain,
  [Chain.Soneium]: UniverseChainId.Soneium,
  [Chain.AstrochainSepolia]: UniverseChainId.UnichainSepolia,
  [Chain.Worldchain]: UniverseChainId.WorldChain,
  [Chain.Zksync]: UniverseChainId.Zksync,
  [Chain.Zora]: UniverseChainId.Zora,
  [Chain.Xlayer]: UniverseChainId.XLayer,
  [Chain.Megaeth]: UniverseChainId.MEGAETHMainnet,
  [Chain.Robinhood]: UniverseChainId.RobinhoodChain
};
export function fromGraphQLChain(chain) {
  return chain != null ? GRAPHQL_CHAIN_TO_UNIVERSE[chain] ?? null : null;
}
export function getPollingIntervalByBlocktime(chainId) {
  return isMainnetChainId(chainId) ? PollingInterval.Fast : PollingInterval.LightningMcQueen;
}
const WEBAPP_LINK_TO_UNIVERSE = {
  [Chain.Ethereum.toLowerCase()]: UniverseChainId.Mainnet,
  [Chain.Arbitrum.toLowerCase()]: UniverseChainId.ArbitrumOne,
  [Chain.Avalanche.toLowerCase()]: UniverseChainId.Avalanche,
  [Chain.Base.toLowerCase()]: UniverseChainId.Base,
  [Chain.Blast.toLowerCase()]: UniverseChainId.Blast,
  [Chain.Bnb.toLowerCase()]: UniverseChainId.Bnb,
  [Chain.Celo.toLowerCase()]: UniverseChainId.Celo,
  [Chain.MonadTestnet.toLowerCase()]: UniverseChainId.MonadTestnet,
  [Chain.Optimism.toLowerCase()]: UniverseChainId.Optimism,
  [Chain.Polygon.toLowerCase()]: UniverseChainId.Polygon,
  [Chain.EthereumSepolia.toLowerCase()]: UniverseChainId.Sepolia,
  [Chain.Unichain.toLowerCase()]: UniverseChainId.Unichain,
  [Chain.Soneium.toLowerCase()]: UniverseChainId.Soneium,
  [Chain.AstrochainSepolia.toLowerCase()]: UniverseChainId.UnichainSepolia,
  [Chain.Worldchain.toLowerCase()]: UniverseChainId.WorldChain,
  [Chain.Zksync.toLowerCase()]: UniverseChainId.Zksync,
  [Chain.Zora.toLowerCase()]: UniverseChainId.Zora,
  [Chain.Xlayer.toLowerCase()]: UniverseChainId.XLayer,
  [Chain.Megaeth.toLowerCase()]: UniverseChainId.MEGAETHMainnet,
  [Chain.Hyper.toLowerCase()]: UniverseChainId.HyperMainnet,
  [Chain.Robinhood.toLowerCase()]: UniverseChainId.RobinhoodChain
};
export function fromUniswapWebAppLink(network) {
  const chainId = network != null ? WEBAPP_LINK_TO_UNIVERSE[network] : void 0;
  if (chainId === void 0) {
    throw new Error(`Network "${network}" can not be mapped`);
  }
  return chainId;
}
const UNIVERSE_TO_WEBAPP_LINK = {
  [UniverseChainId.Mainnet]: Chain.Ethereum.toLowerCase(),
  [UniverseChainId.ArbitrumOne]: Chain.Arbitrum.toLowerCase(),
  [UniverseChainId.Avalanche]: Chain.Avalanche.toLowerCase(),
  [UniverseChainId.Base]: Chain.Base.toLowerCase(),
  [UniverseChainId.Blast]: Chain.Blast.toLowerCase(),
  [UniverseChainId.Bnb]: Chain.Bnb.toLowerCase(),
  [UniverseChainId.Celo]: Chain.Celo.toLowerCase(),
  [UniverseChainId.MonadTestnet]: Chain.MonadTestnet.toLowerCase(),
  [UniverseChainId.Optimism]: Chain.Optimism.toLowerCase(),
  [UniverseChainId.Polygon]: Chain.Polygon.toLowerCase(),
  [UniverseChainId.Sepolia]: Chain.EthereumSepolia.toLowerCase(),
  [UniverseChainId.Unichain]: Chain.Unichain.toLowerCase(),
  [UniverseChainId.Soneium]: Chain.Soneium.toLowerCase(),
  [UniverseChainId.UnichainSepolia]: Chain.AstrochainSepolia.toLowerCase(),
  [UniverseChainId.WorldChain]: Chain.Worldchain.toLowerCase(),
  [UniverseChainId.Zksync]: Chain.Zksync.toLowerCase(),
  [UniverseChainId.Zora]: Chain.Zora.toLowerCase(),
  [UniverseChainId.XLayer]: Chain.Xlayer.toLowerCase(),
  [UniverseChainId.MEGAETHMainnet]: Chain.Megaeth.toLowerCase(),
  [UniverseChainId.HyperMainnet]: Chain.Hyper.toLowerCase(),
  [UniverseChainId.RobinhoodChain]: Chain.Robinhood.toLowerCase()
};
export function toUniswapWebAppLink(chainId) {
  const link = UNIVERSE_TO_WEBAPP_LINK[chainId];
  if (link === void 0) {
    throw new Error(`ChainID "${chainId}" can not be mapped`);
  }
  return link;
}
export function filterChainIdsByFeatureFlag(featureFlaggedChainIds) {
  return ALL_CHAIN_IDS.filter((chainId) => {
    return featureFlaggedChainIds[chainId] ?? true;
  });
}
export function getEnabledChains({
  isTestnetModeEnabled,
  featureFlaggedChainIds,
  connectedWalletChainIds
}) {
  if (isTestnetModeEnabled) {
    const supportedTestnetChainIds = SUPPORTED_TESTNET_CHAIN_IDS.filter(
      (chainId) => featureFlaggedChainIds.includes(chainId) && (connectedWalletChainIds ? connectedWalletChainIds.includes(chainId) : true)
    );
    return {
      chains: supportedTestnetChainIds,
      gqlChains: GQL_TESTNET_CHAINS,
      defaultChainId: UniverseChainId.Sepolia,
      isTestnetModeEnabled
    };
  }
  const supportedChainIds = SUPPORTED_CHAIN_IDS.filter(
    (chainId) => featureFlaggedChainIds.includes(chainId) && (connectedWalletChainIds ? connectedWalletChainIds.includes(chainId) : true)
  );
  const supportedGqlChains = GQL_MAINNET_CHAINS.filter((chain) => {
    const chainId = fromGraphQLChain(chain);
    return chainId && supportedChainIds.includes(chainId);
  });
  return {
    chains: supportedChainIds,
    gqlChains: supportedGqlChains,
    defaultChainId: UniverseChainId.Mainnet,
    isTestnetModeEnabled
  };
}
